import { useState } from "react";
import { Form, Input, Button, message } from "antd";
import {
  MailOutlined,
  PhoneOutlined,
  WhatsAppOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";
import "./ContactUs.css";
import SEO from "../SEO/SEO";
import { OFFICES } from "../../data/locations";

// ── Get a free access key at https://web3forms.com/ (signup with contact@garvana.com)
//    Replace the placeholder below with your real key. Until you do, the email
//    button gracefully falls back to asking the user to use WhatsApp.
const WEB3FORMS_ACCESS_KEY = "177b492a-0669-447d-9ce6-c84c01717997";
const WHATSAPP_NUMBER = "919800040505"; // International format, no + or spaces

export default function ContactUs() {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const onFinish = async (values) => {
    if (!WEB3FORMS_ACCESS_KEY || WEB3FORMS_ACCESS_KEY === "YOUR_WEB3FORMS_ACCESS_KEY_HERE") {
      message.warning("Email is not configured yet. Please use WhatsApp or call +91-9800040505.");
      return;
    }

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("access_key", WEB3FORMS_ACCESS_KEY);
      fd.append("subject", `Garvaana — New Inquiry from ${values.fullName}`);
      fd.append("from_name", values.fullName);
      fd.append("email", values.email);
      fd.append("phone", values.phone);
      fd.append("company", values.company || "—");
      fd.append("service", values.subject);
      fd.append("message", values.message);

      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: fd,
      });
      const data = await res.json();

      if (data.success) {
        message.success("Message sent. We'll respond within 24 hours.");
        form.resetFields();
      } else {
        message.error("Could not send. Please try WhatsApp or call us directly.");
      }
    } catch (err) {
      message.error("Network error. Please try WhatsApp or call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const sendViaWhatsApp = async () => {
    try {
      const values = await form.validateFields();
      const text =
        `*New Inquiry — Garvaana*\n\n` +
        `*Name:* ${values.fullName}\n` +
        `*Phone:* ${values.phone}\n` +
        `*Email:* ${values.email}\n` +
        `*Company / Property:* ${values.company || "—"}\n` +
        `*Service Required:* ${values.subject}\n\n` +
        `*Project Details:*\n${values.message}`;
      const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`;
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      // antd shows the validation errors inline
    }
  };

  const openMaps = (url) => window.open(url, "_blank", "noopener,noreferrer");

  return (
    <div className="contact-page">
      <SEO path="/contact" />
      {/* ── HERO HEADER ── */}
      <div className="contact-page-header">
        <span className="contact-page-tag">Get In Touch</span>
        <h1 className="contact-page-title">
          Let's Create Something <em>Extraordinary</em>
        </h1>
        <p className="contact-page-subtitle">
          Reach out to begin your landscape journey. We respond within 24 hours.
        </p>
        <span className="contact-page-divider" />
      </div>

      {/* ── BODY ── */}
      <div className="contact-body">
        <div className="contact-row">

          {/* ── INFO PANEL ── */}
          <div className="contact-info-panel">
            <h2 className="contact-panel-title">Contact Information</h2>
            <p className="contact-panel-sub">
              We'd love to hear from you. Reach us through any of these channels.
            </p>
            <span className="contact-panel-divider" />

            <div className="contact-info-list">

              <div
                className="contact-info-item"
                onClick={() => openMaps(OFFICES[0].mapsUrl)}
                style={{ cursor: "pointer" }}
              >
                <div className="contact-info-icon">
                  <EnvironmentOutlined />
                </div>
                <div className="contact-info-text">
                  <span className="contact-info-label">Head Office &amp; Nursery — Gajraula</span>
                  <span className="contact-info-value">
                    Vill - Manota, Hasanpur Road,<br />
                    Gajraula, Amroha, UP 244241
                  </span>
                </div>
              </div>

              <div
                className="contact-info-item"
                onClick={() => openMaps(OFFICES[1].mapsUrl)}
                style={{ cursor: "pointer" }}
              >
                <div className="contact-info-icon">
                  <EnvironmentOutlined />
                </div>
                <div className="contact-info-text">
                  <span className="contact-info-label">Studio &amp; Office — Noida</span>
                  <span className="contact-info-value">
                    Noida, Uttar Pradesh<br />
                    (Serving all of Delhi NCR)
                  </span>
                </div>
              </div>

              <a href="tel:+919800040505" className="contact-info-item">
                <div className="contact-info-icon">
                  <PhoneOutlined />
                </div>
                <div className="contact-info-text">
                  <span className="contact-info-label">Call Us</span>
                  <span className="contact-info-value">+91-9800040505</span>
                </div>
              </a>

              <a href="mailto:contact@garvana.com" className="contact-info-item">
                <div className="contact-info-icon">
                  <MailOutlined />
                </div>
                <div className="contact-info-text">
                  <span className="contact-info-label">Email Us</span>
                  <span className="contact-info-value">contact@garvana.com</span>
                </div>
              </a>

              <a
                href="https://wa.me/919800040505"
                target="_blank"
                rel="noopener noreferrer"
                className="contact-info-item"
              >
                <div className="contact-info-icon contact-info-icon--whatsapp">
                  <WhatsAppOutlined />
                </div>
                <div className="contact-info-text">
                  <span className="contact-info-label">WhatsApp</span>
                  <span className="contact-info-value">Chat with us now</span>
                </div>
              </a>

            </div>
          </div>

          {/* ── FORM PANEL ── */}
          <div className="contact-form-panel">
            <h2 className="contact-panel-title">Request a Consultation</h2>
            <p className="contact-panel-sub">
              Tell us about your project and we'll get back to you within 24 hours.
            </p>
            <span className="contact-panel-divider" />

            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              className="contact-form"
            >
              <div className="contact-form-row">
                <Form.Item
                  name="fullName"
                  label="Full Name"
                  rules={[{ required: true, message: "Please enter your name" }]}
                >
                  <Input placeholder="Your full name" />
                </Form.Item>
                <Form.Item name="company" label="Company / Property">
                  <Input placeholder="Optional" />
                </Form.Item>
              </div>

              <div className="contact-form-row">
                <Form.Item
                  name="email"
                  label="Email Address"
                  rules={[{ required: true, type: "email", message: "Enter a valid email" }]}
                >
                  <Input placeholder="your@email.com" />
                </Form.Item>
                <Form.Item
                  name="phone"
                  label="Phone Number"
                  rules={[{ required: true, message: "Enter your phone number" }]}
                >
                  <Input placeholder="+91 XXXXX XXXXX" />
                </Form.Item>
              </div>

              <Form.Item
                name="subject"
                label="Service Required"
                rules={[{ required: true, message: "Please enter a subject" }]}
              >
                <Input placeholder="e.g. Landscape Design for Villa, Rooftop Garden, Corporate Campus..." />
              </Form.Item>

              <Form.Item
                name="message"
                label="Project Details"
                rules={[{ required: true, message: "Please describe your project" }]}
              >
                <Input.TextArea
                  rows={5}
                  placeholder="Tell us about your space, size, requirements, and vision..."
                />
              </Form.Item>

              <div className="contact-submit-row">
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={submitting}
                  className="contact-submit-btn"
                >
                  ✦ Send via Email
                </Button>
                <Button
                  type="default"
                  onClick={sendViaWhatsApp}
                  icon={<WhatsAppOutlined />}
                  className="contact-whatsapp-btn"
                >
                  Send via WhatsApp
                </Button>
              </div>
            </Form>
          </div>

        </div>
      </div>

    </div>
  );
}